/**
 * Workspace Standard Card Component
 *
 * Displays a workspace-level official standard with status badge and actions.
 */

import { Pencil, Trash2, RefreshCw, FileText } from "lucide-react";
import { Button } from "@/components/ui/primitives";
import { useTranslation } from "@/packages/utils";
import type { Id } from "@/packages/types";
import { StatusBadge, formatDate } from "./shared";

/* ------------------------------------------------------------------ */
/* Types                                                               */
/* ------------------------------------------------------------------ */

interface WorkspaceStandardCardProps {
  standard: {
    _id: Id<"officialStandards">;
    fileName: string;
    description?: string;
    status: string;
    extractedClausesCount: number;
    creatorName: string;
    _creationTime: number;
  };
  onClick: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onReupload?: () => void;
}

/* ------------------------------------------------------------------ */
/* WorkspaceStandardCard                                               */
/* ------------------------------------------------------------------ */

export function WorkspaceStandardCard({
  standard,
  onClick,
  onEdit,
  onDelete,
  onReupload,
}: WorkspaceStandardCardProps) {
  const { t } = useTranslation("common");
  const isFailed = standard.status === "extraction_failed";

  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full text-left rounded-xl border border-border bg-card p-4 transition-colors hover:border-accent/50 cursor-pointer"
    >
      {/* Row 1: Title + Status badge */}
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2 min-w-0">
          <FileText size={16} className="text-accent/70 shrink-0" />
          <span className="text-[14px] font-semibold text-primary truncate">
            {standard.fileName}
          </span>
        </div>
        <StatusBadge status={standard.status} />
      </div>

      {/* Row 2: Description, clause count, creator */}
      <div className="mb-3 space-y-1">
        {standard.description && (
          <p className="text-[13px] text-secondary line-clamp-2">
            {standard.description}
          </p>
        )}
        <div className="flex items-center gap-3 text-[12px] text-muted">
          {standard.extractedClausesCount > 0 && (
            <span>
              {t("workspaceStandards.card.clauses", {
                count: standard.extractedClausesCount,
              })}
            </span>
          )}
          <span>{standard.creatorName}</span>
          <span>{formatDate(standard._creationTime)}</span>
        </div>
      </div>

      {/* Row 3: Actions */}
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: stop propagation wrapper */}
      {/* biome-ignore lint/a11y/noStaticElementInteractions: stop propagation wrapper */}
      <div
        className="flex items-center gap-1"
        onClick={(e) => e.stopPropagation()}
      >
        <Button variant="ghost" size="sm" icon={Pencil} onClick={onEdit}>
          {t("workspaceStandards.card.actions.edit")}
        </Button>

        <Button variant="danger" size="sm" icon={Trash2} onClick={onDelete}>
          {t("workspaceStandards.card.actions.delete")}
        </Button>

        {isFailed && onReupload && (
          <Button
            variant="secondary"
            size="sm"
            icon={RefreshCw}
            onClick={onReupload}
          >
            {t("workspaceStandards.card.actions.reupload")}
          </Button>
        )}
      </div>
    </button>
  );
}
